import { MirrorsGame } from '../../games/mirrors/MirrorsGame.js';
import { MirrorsVortex } from '../../games/mirrors/MirrorsVortex.js';
import { MirrorsRenderer } from '../../games/mirrors/MirrorsRenderer.js';
import { Grid } from '../../core/Grid.js';
import { Position, positionKey } from '../../core/GridGeometry.js';
import { RenderConfig, COLORS } from '../../rendering/Renderer.js';
import { TutorialGameAdapter } from '../TutorialEngine.js';
import { isVortexGame } from '../../games/IVortexGame.js';

// ── Mirrors-specific types ────────────────────────────────────────

export interface MirrorsRenderOptions {
    showLaser?: boolean;
    showReverseLaser?: boolean;
}

// ── Renderer (internal) ───────────────────────────────────────────

class MirrorsTutorialRenderer extends MirrorsRenderer {
    renderTutorial(game: MirrorsGame, options: MirrorsRenderOptions = {}): void {
        const showLaser = options.showLaser ?? true;
        const showReverseLaser = options.showReverseLaser ?? true;
        const grid = game.getGrid();

        const mainPath = game.traceLaser();
        const mainAnalysis = this.analyzeMainBeam(grid, mainPath);

        if (game.checkWin()) {
            mainAnalysis.lastValidSegmentIdx = mainPath.segments.length - 1;
        }

        this.clear();
        this.drawGrid();

        if (showReverseLaser) {
            const reversePath = game.traceReverseLaser();
            const hintAnalysis = this.analyzeHintBeam(grid, reversePath);
            this.drawReverseLaser(reversePath, hintAnalysis);
            this.drawBeamTriangles(game, reversePath, COLORS.amber, true);
        }

        if (showLaser) {
            this.drawLaser(mainPath, mainAnalysis);
            this.drawBeamTriangles(game, mainPath, (segIdx) => {
                if (mainAnalysis.firstViolationSegmentIdx >= 0 && segIdx >= mainAnalysis.firstViolationSegmentIdx) {
                    return COLORS.red;
                }
                return COLORS.teal;
            });
        }

        this.drawEntryExitMarkers(game);
        this.drawMirrors(game);
        this.drawWarnings(game);
        this.drawDigits(grid);

        const hintAnalysis = showReverseLaser
            ? this.analyzeHintBeam(grid, game.traceReverseLaser())
            : null;
        this.drawDigitBadges(game, mainAnalysis, hintAnalysis);
    }
}

// ── Adapter ───────────────────────────────────────────────────────

export class MirrorsTutorialAdapter implements TutorialGameAdapter {
    private game: MirrorsGame;
    private renderer: MirrorsTutorialRenderer;

    constructor(canvas: HTMLCanvasElement, grid: Grid) {
        this.game = new MirrorsGame(grid);
        this.renderer = new MirrorsTutorialRenderer(canvas, grid);
        // Static scenes: no mirror tween, so the renderer never self-re-renders
        // and wipes the engine-drawn annotations.
        this.renderer.setSelfAnimation(false);
    }

    applyState(state: unknown): void {
        const serialized = state as string;
        this.game.reset();
        if (serialized) {
            this.game.deserialize(serialized);
        }
    }

    render(options: unknown): void {
        this.renderer.renderTutorial(this.game, options as MirrorsRenderOptions);
    }

    getConfig(): RenderConfig {
        return this.renderer.getConfig();
    }
}

export function createMirrorsTutorialAdapter(
    canvas: HTMLCanvasElement,
    grid: Grid,
): TutorialGameAdapter {
    return new MirrorsTutorialAdapter(canvas, grid);
}

// ── Vortex state ─────────────────────────────────────────────────

export interface MirrorsVortexTutorialState {
    mirrors: string;
    gateMode?: boolean;
    /**
     * Extra gate pairs (cell strings, e.g. ['B5', 'D5']) added on top of the
     * adapter's default teleport pair for this scene only. Used by the
     * activation scene to show a second, blocked (amber) pair alongside the
     * main pair. Omit to use just the default pair.
     */
    extraGates?: Array<[string, string]>;
}

/** Parse a cell string like "B5" into a Position ({ row: 5, col: 1 }). */
function parseCellString(s: string): Position {
    return { col: s.charCodeAt(0) - 65, row: parseInt(s.slice(1), 10) };
}

// ── Vortex Renderer (internal) ───────────────────────────────────

class MirrorsVortexTutorialRenderer extends MirrorsRenderer {
    private tutorialGateMode: boolean = false;

    setTutorialGateMode(enabled: boolean): void {
        this.tutorialGateMode = enabled;
        this.setGateMode(enabled);
    }

    renderTutorial(game: MirrorsGame, options: MirrorsRenderOptions = {}): void {
        const showLaser = options.showLaser ?? true;
        const showReverseLaser = options.showReverseLaser ?? true;
        const grid = game.getGrid();

        const mainPath = game.traceLaser();
        const mainAnalysis = this.analyzeMainBeam(grid, mainPath);

        if (game.checkWin()) {
            mainAnalysis.lastValidSegmentIdx = mainPath.segments.length - 1;
        }

        this.clear();
        this.drawGrid();

        if (showReverseLaser) {
            const reversePath = game.traceReverseLaser();
            const hintAnalysis = this.analyzeHintBeam(grid, reversePath);
            this.drawReverseLaser(reversePath, hintAnalysis);
            this.drawBeamTriangles(game, reversePath, COLORS.amber, true);
        }

        if (showLaser) {
            this.drawLaser(mainPath, mainAnalysis);
            this.drawBeamTriangles(game, mainPath, (segIdx) => {
                if (mainAnalysis.firstViolationSegmentIdx >= 0 && segIdx >= mainAnalysis.firstViolationSegmentIdx) {
                    return COLORS.red;
                }
                return COLORS.teal;
            });
        }

        this.drawEntryExitMarkers(game);
        if (isVortexGame(game)) {
            this.drawActiveVortexGates(game);
        }
        this.drawMirrors(game);
        this.drawWarnings(game);
        this.drawDigits(grid);

        const hintAnalysis = showReverseLaser
            ? this.analyzeHintBeam(grid, game.traceReverseLaser())
            : null;
        this.drawDigitBadges(game, mainAnalysis, hintAnalysis);

        if (this.tutorialGateMode && isVortexGame(game)) {
            this.drawGateModeHighlights(game);
        }
    }
}

// ── Vortex Adapter ───────────────────────────────────────────────

export class MirrorsVortexTutorialAdapter implements TutorialGameAdapter {
    private game: MirrorsVortex;
    private renderer: MirrorsVortexTutorialRenderer;
    private gatePairs: Array<[Position, Position]>;
    private allGates: Set<string>;
    private currentGateMode: boolean = false;

    constructor(
        canvas: HTMLCanvasElement,
        grid: Grid,
        gatePairs: Array<[Position, Position]>,
    ) {
        this.game = new MirrorsVortex(grid);
        this.renderer = new MirrorsVortexTutorialRenderer(canvas, grid);
        this.renderer.setSelfAnimation(false);   // static scenes — see the Base adapter
        this.gatePairs = gatePairs;
        this.allGates = new Set<string>();
        for (const [a, b] of gatePairs) {
            this.allGates.add(positionKey(a));
            this.allGates.add(positionKey(b));
        }
    }

    applyState(state: unknown): void {
        const s = state as MirrorsVortexTutorialState | null;
        this.game.reset();
        const pairs = [...this.gatePairs];
        if (s?.extraGates) {
            for (const [a, b] of s.extraGates) {
                pairs.push([parseCellString(a), parseCellString(b)]);
            }
        }
        const allGates = new Set<string>();
        for (const [a, b] of pairs) {
            allGates.add(positionKey(a));
            allGates.add(positionKey(b));
        }
        this.game.setVortexData(pairs, allGates);
        if (s?.mirrors) {
            this.game.deserialize(s.mirrors);
        }
        this.currentGateMode = s?.gateMode ?? false;
    }

    render(options: unknown): void {
        this.renderer.setTutorialGateMode(this.currentGateMode);
        this.renderer.renderTutorial(this.game, options as MirrorsRenderOptions);
    }

    getConfig(): RenderConfig {
        return this.renderer.getConfig();
    }
}

export function createMirrorsVortexTutorialAdapter(
    canvas: HTMLCanvasElement,
    grid: Grid,
): TutorialGameAdapter {
    // Gate pair for the vortex mirrors tutorial: E1 ↔ C2
    const gatePairs: Array<[Position, Position]> = [
        [{ row: 1, col: 4 }, { row: 2, col: 2 }],
    ];
    return new MirrorsVortexTutorialAdapter(canvas, grid, gatePairs);
}
